/**
 * CORE-BL — Guided Onboarding & Product Education
 * The one-shot first-use invitation.
 *
 * WHY IT IS SHOP-SCOPED NOW
 * -------------------------
 * The invitation used to be `localStorage['startTutorialOnLoad'] = 'true'` —
 * a bare boolean with no idea what it was an invitation TO.
 *
 * That was survivable only while delivery lived on the dashboard, which is
 * one shop's page. Delivery is now global, and a person can belong to several
 * shops: accept an invitation to shop A, land somewhere with shop B active,
 * and the boolean would have been spent starting B's walkthrough. The one
 * automatic invitation to the shop they had just joined would be gone, and
 * they would never be taught the job they were actually invited to do.
 *
 * The invitation now names its shop, and delivery only acts on it while that
 * shop is the active one.
 *
 * ONE AUTHORITY
 * -------------
 * Three places used to touch the raw key with their own string literals
 * (`OnboardShop` set it, `Dashboard` read and removed it, tests wrote it).
 * Every one of them had to agree about a format nobody had written down.
 * They all go through here instead.
 */

import type { StartRefusalReason } from '@/contexts/TutorialContext';

/**
 * The storage key, unchanged.
 *
 * Kept so an invitation issued before this change — sitting in somebody's
 * browser right now — is still found rather than silently dropped.
 */
export const FIRST_USE_FLAG = 'startTutorialOnLoad';

/** Marks a stored value written before invitations named their shop. */
export const LEGACY_FIRST_USE_VALUE = 'true';

export type FirstUseInvitation =
  /** Issued for a specific shop. */
  | { kind: 'shop'; shopId: string }
  /**
   * The historical bare `'true'`. It names no shop, so it is honoured once
   * for whichever shop is active when it is found — which is exactly what the
   * old code did, and is the most that value can support.
   */
  | { kind: 'legacy' };

/** Issue the invitation for a specific shop. Safe if storage is unavailable. */
export function issueFirstUse(shopId: string): void {
  if (!shopId) return;
  try {
    localStorage.setItem(FIRST_USE_FLAG, JSON.stringify({ shopId }));
  } catch {
    // A storage failure must not take the join or setup flow down with it.
  }
}

/** Read the pending invitation, or null. Never throws. */
export function readFirstUse(): FirstUseInvitation | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(FIRST_USE_FLAG);
  } catch {
    return null;
  }
  if (!raw) return null;
  if (raw === LEGACY_FIRST_USE_VALUE) return { kind: 'legacy' };
  try {
    const parsed = JSON.parse(raw) as { shopId?: unknown };
    if (typeof parsed?.shopId === 'string' && parsed.shopId) {
      return { kind: 'shop', shopId: parsed.shopId };
    }
  } catch {
    // Unreadable content is not an invitation. Fall through.
  }
  return null;
}

/**
 * Whether a pending invitation applies to the shop currently active.
 *
 * A legacy invitation names no shop and therefore applies to whichever one is
 * active — that is all the old format can say, and pretending otherwise would
 * either drop it or invent a shop for it.
 */
export function invitationAppliesTo(
  invitation: FirstUseInvitation | null,
  activeShopId: string | null | undefined,
): boolean {
  if (!invitation || !activeShopId) return false;
  if (invitation.kind === 'legacy') return true;
  return invitation.shopId === activeShopId;
}

/**
 * Spend the invitation — but only if it is the one being spent.
 *
 * The stored value is re-read and compared before removal, so a delivery
 * decision made about shop A cannot consume an invitation that has since been
 * re-issued for shop B.
 */
export function consumeFirstUse(activeShopId: string | null | undefined): void {
  const pending = readFirstUse();
  if (!invitationAppliesTo(pending, activeShopId)) return;
  try {
    localStorage.removeItem(FIRST_USE_FLAG);
  } catch {
    // As above.
  }
}

/**
 * An identity for the pending invitation, for guards that must reset when a
 * NEW invitation arrives.
 *
 * The coordinator's "already handled this" refs were mount-lifetime booleans,
 * and it is now mounted for the whole session — so one settled invitation
 * silenced every later one. Keying those guards to this string makes the
 * coordinator idempotent per invitation rather than per mount.
 */
export function invitationKey(
  invitation: FirstUseInvitation | null,
): string | null {
  if (!invitation) return null;
  return invitation.kind === 'legacy' ? 'legacy' : `shop:${invitation.shopId}`;
}

/**
 * Whether a refusal is a final answer about this person, or a moment to wait
 * through.
 *
 *   no_content     conclusive. Nothing is authored for their experience.
 *   route_blocked  conclusive TOO, but for a different reason: the lessons
 *                  exist and the router admits this role to none of the pages
 *                  they happen on. Retrying on every page load will not
 *                  create a route. The person is TOLD which of the two it is
 *                  before the invitation is spent — see the coordinator.
 *
 *   experience_unknown  the membership read has not settled, or could not
 *   progress_unread     tell. KEEP.
 *   progress_failed     the stored-progress read failed. KEEP — this is the
 *                       database having a bad moment, and consuming here is
 *                       precisely how somebody loses their one invitation to
 *                       a transient error.
 *   no_session          not signed in to a shop yet. KEEP.
 *   save_failed         the tutorial is running in memory and did not reach
 *                       the database. KEEP, so it is offered again rather
 *                       than recorded as delivered when it was not.
 */
export function consumesFlag(reason: StartRefusalReason): boolean {
  return reason === 'no_content' || reason === 'route_blocked';
}
